import { FC, KeyboardEvent, MouseEvent, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Skeleton, Tooltip } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import { Bar, Pie } from 'react-chartjs-2';
import type { ChartOptions } from 'chart.js';
import { QcsButton } from '../../common/basic/QcsButton';
import { EmptyState, SectionLabel } from './AnalyticsSharedStyles';
import { useAppSelector } from '../../../store';
import { selectAnalyticsEntry } from '../../../store/entities/analytics';
import { FetchState } from '../../../store/fetchState';
import { AnalyticsMode } from '@api/models/analytics/AnalyticsMode';
import { DashboardChartType } from '@api/models/enums/DashboardChartType';
import { ANALYTICS_AXIS_COLOR, ANALYTICS_PIE_BORDER, paletteFor } from './AnalyticsColors';
import { baseBarOptions, basePieOptions } from './AnalyticsChartDefaults';
import { MiniChartBadge, MiniChartTile } from '../AnalyticsStyles';
import { formatCellValue } from '../analyticsColumns';
import { sortGroupsByValueDesc } from './sortGroupsByValue';
import { formatGroupLabel } from '../analyticsDateUtils';

type MiniChartKind = 'bar' | 'pie';

const chartTypeToKind = (ct?: DashboardChartType): MiniChartKind => {
  if (ct === DashboardChartType.Pie) return 'pie';
  return 'bar';
};

interface Props {
  titleKey?: string;
  title?: string;
  reduxKey: string;
  groupBy?: string;
  onClick: () => void;
  onRetry: () => void;
  metric?: string;
  xAxisLabel?: string;
  sortByValue?: boolean;
  badgeText?: string;
  chartType?: DashboardChartType;
  actions?: ReactNode;
}

const CHART_HEIGHT = 280;

export const AnalyticsMiniChart: FC<Props> = ({
  titleKey,
  title: titleProp,
  reduxKey,
  groupBy,
  onClick,
  onRetry,
  metric,
  xAxisLabel,
  sortByValue,
  badgeText,
  chartType,
  actions: actionSlot,
}) => {
  const { t, i18n } = useTranslation();
  const [chartKindOverride, setChartKindOverride] = useState<MiniChartKind | null>(null);
  const [prevChartType, setPrevChartType] = useState(chartType);
  if (prevChartType !== chartType) {
    setPrevChartType(chartType);
    setChartKindOverride(null);
  }
  const chartKind = chartKindOverride ?? chartTypeToKind(chartType);
  const entry = useAppSelector(selectAnalyticsEntry(reduxKey));
  const loading = entry.state === FetchState.Loading;
  const hasError = entry.state === FetchState.Error;
  const data = entry.data;
  const groupsData = data?.mode === AnalyticsMode.Groups ? data : undefined;

  const groups = useMemo(() => groupsData?.groups ?? [], [groupsData]);

  const valueKey = metric ?? 'count';
  const yCol = groupsData?.columns?.find((c) => c.key === valueKey);
  const yAxisLabel = yCol ? t(yCol.labelKey) : undefined;

  const displayGroups = useMemo(
    () => (sortByValue ? sortGroupsByValueDesc(groups, valueKey) : groups),
    [groups, valueKey, sortByValue],
  );

  const labels = useMemo(
    () => displayGroups.map((g, idx) => formatGroupLabel(g.groupKey, groupBy ?? '', `#${idx + 1}`, i18n)),
    [displayGroups, groupBy, i18n],
  );

  const values = useMemo(
    () =>
      displayGroups.map((g) => {
        const v = g.values?.[valueKey];
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      }),
    [displayGroups, valueKey],
  );

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: paletteFor(labels),
          borderRadius: 6,
          maxBarThickness: 36,
        },
      ],
    }),
    [labels, values],
  );

  const pieData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: paletteFor(labels),
          borderColor: ANALYTICS_PIE_BORDER,
          borderWidth: 2,
        },
      ],
    }),
    [labels, values],
  );

  // Axis titles still say what each axis shows (group-by dimension / metric).
  const axisTitleFor = (text?: string) =>
    text ? { title: { display: true, text, color: ANALYTICS_AXIS_COLOR, font: { size: 11 } } } : {};

  // Tooltip line "Metric: value", value formatted by column type (counts localized, durations as h/m, …).
  // chart.js types a bar's parsed value as nullable; our data never is, so 0 is a safe fallback.
  const tooltipLabel = (value: number | null) => {
    const n = value ?? 0;
    const formatted = yCol ? formatCellValue(n, yCol.type, i18n) : String(n);
    return yAxisLabel ? `${yAxisLabel}: ${formatted}` : formatted;
  };

  const barOptions: ChartOptions<'bar'> = {
    ...baseBarOptions,
    plugins: {
      ...baseBarOptions.plugins,
      tooltip: {
        ...baseBarOptions.plugins!.tooltip,
        callbacks: { label: (ctx) => tooltipLabel(ctx.parsed.y) },
      },
    },
    scales: {
      ...baseBarOptions.scales,
      x: {
        ...baseBarOptions.scales!.x,
        ticks: { display: true, color: ANALYTICS_AXIS_COLOR, font: { size: 10 }, autoSkip: false },
        ...axisTitleFor(xAxisLabel),
      },
      y: {
        ...baseBarOptions.scales!.y,
        ...axisTitleFor(yAxisLabel),
        ticks: {
          ...baseBarOptions.scales!.y!.ticks,
          // Duration metrics: axis ticks as hh:mm:ss instead of raw seconds.
          ...(yCol?.type === 'DURATION_SEC' ? { callback: (v: string | number) => formatCellValue(Number(v), 'DURATION_SEC', i18n) } : {}),
        },
      },
    },
  };

  const pieOptions: ChartOptions<'pie'> = {
    ...basePieOptions,
    plugins: {
      ...basePieOptions.plugins,
      tooltip: {
        ...basePieOptions.plugins!.tooltip,
        callbacks: {
          title: (items) => items[0]?.label ?? '',
          label: (ctx) => tooltipLabel(ctx.parsed),
        },
      },
    },
  };

  const handleToggleChartKind = (e: MouseEvent) => {
    e.stopPropagation();
    setChartKindOverride(chartKind === 'bar' ? 'pie' : 'bar');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div {...(hasError ? {} : { role: 'button', tabIndex: 0, onClick, onKeyDown: handleKeyDown })} style={{ outline: 'none' }}>
      <MiniChartTile>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <SectionLabel>{titleProp ?? (titleKey ? t(titleKey) : '')}</SectionLabel>
            {badgeText && <MiniChartBadge>{badgeText}</MiniChartBadge>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            {actionSlot}
            {!loading && !hasError && groups.length > 0 && (
              <Tooltip title={t(chartKind === 'bar' ? 'analytics.dashboard.showPie' : 'analytics.dashboard.showBar')}>
                <IconButton size="small" onClick={handleToggleChartKind}>
                  {chartKind === 'bar' ? <PieChartIcon fontSize="small" /> : <BarChartIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>

        {loading && <Skeleton variant="rectangular" height={CHART_HEIGHT} sx={{ borderRadius: '8px' }} />}

        {hasError && (
          <EmptyState style={{ flex: 1 }}>
            <span>{t('analytics.dashboard.noData')}</span>
            <QcsButton variant="outlined" stopPropagation onClick={() => onRetry()}>
              {t('analytics.dashboard.retry')}
            </QcsButton>
          </EmptyState>
        )}

        {!loading && !hasError && groups.length === 0 && entry.state === FetchState.Loaded && (
          <EmptyState style={{ flex: 1 }}>{t('analytics.dashboard.noData')}</EmptyState>
        )}

        {!loading && !hasError && groups.length > 0 && (
          <div style={{ height: CHART_HEIGHT }}>
            {chartKind === 'bar' ? <Bar data={chartData} options={barOptions} /> : <Pie data={pieData} options={pieOptions} />}
          </div>
        )}
      </MiniChartTile>
    </div>
  );
};
